import { useEffect, useContext, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faChevronLeft, faUserPlus, faUserXmark, faXmark } from '@fortawesome/free-solid-svg-icons';
import AddUserConfirmationDialog from '../components/dialogs/AddUserConfirmationDialog';
import ApproveNewUserRequestDialog from '../components/dialogs/ApproveNewUserRequestDialog';
import searchIcon from '../assets/searchicon.png';
import DeleteConfirmationDialog from '../components/dialogs/DeleteConfirmationDialog';
import TimedDialog from '../components/dialogs/TimedDialog';
import { UserContext } from '../context/UserContext';
import CommonLayout from '../components/CommonLayout';
import FloatingAddButton from '../components/FloatingAddButton';
import API from '../config/axiosConfig';
import UserActionsMenu from '../components/UserActionsMenu';
import UsersSkeleton from '../components/Loaders/UsersSkeleton';
import InputWithIcon from '../components/InputWithIcon';
import { getAdminNote } from '../utils/admin-role-note';
import './Users.scss';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [approveFromTokenOpen, setApproveFromTokenOpen] = useState(false);
  const [tokenToApprove, setTokenToApprove] = useState(null);
  const [requesterName, setRequesterName] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('USER');
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [requestToReject, setRequestToReject] = useState(null);
  const [isJWTApproval, setIsJWTApproval] = useState(false);
  const { user, hospitals, fetchPendingRequests: fetchPendingRequestsFromContext } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedHospitals, setExpandedHospitals] = useState([]);
  const [expandedDepartments, setExpandedDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const getUsersForDepartment = (departmentId) => {
    return users.filter(u => u.roles.some(r => r.department === departmentId || r.department?._id === departmentId));
  };

  const { filteredUsers, orphanedUsers, departmentUserMap } = useMemo(() => {
    const orphaned = user?.isSuperAdmin ? users.filter(u => (!u.roles || u.roles.length === 0) && !u.isSuperAdmin) : [];

    if (!searchTerm) {
      const initialDepartmentUserMap = new Map();
      hospitals.forEach(h => {
        h.departments.forEach(d => {
          initialDepartmentUserMap.set(d._id, getUsersForDepartment(d._id));
        });
      });
      return {
        filteredUsers: users,
        orphanedUsers: orphaned,
        departmentUserMap: initialDepartmentUserMap
      };
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const filtered = users.filter(u =>
      u.name.toLowerCase().includes(lowerCaseSearchTerm) ||
      u.email.toLowerCase().includes(lowerCaseSearchTerm)
    );
    
    const newDepartmentUserMap = new Map();
    hospitals.forEach(h => {
      h.departments.forEach(d => {
        newDepartmentUserMap.set(d._id, filtered.filter(u => u.roles.some(r => r.department === d._id || r.department?._id === d._id)));
      });
    });

    return {
      filteredUsers: filtered,
      orphanedUsers: orphaned.filter(u =>
        u.name.toLowerCase().includes(lowerCaseSearchTerm) ||
        u.email.toLowerCase().includes(lowerCaseSearchTerm)
      ),
      departmentUserMap: newDepartmentUserMap
    };
  }, [searchTerm, users, user, hospitals]);

  useEffect(() => {
    if (searchTerm) {
      const hospitalIds = new Set();
      const departmentIds = new Set();

      filteredUsers.forEach(u => {
        if (u.isSuperAdmin) {
          hospitalIds.add('super-admins');
        }
        u.roles.forEach(role => {
          if (role.hospital) hospitalIds.add(role.hospital._id || role.hospital);
          if (role.department) departmentIds.add(role.department._id || role.department);
        });
      });

      if (orphanedUsers.length > 0) {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const hasOrphan = orphanedUsers.some(u => u.name.toLowerCase().includes(lowerCaseSearchTerm) || u.email.toLowerCase().includes(lowerCaseSearchTerm));
        if (hasOrphan) {
          hospitalIds.add('orphaned-users');
        }
      }

      setExpandedHospitals(Array.from(hospitalIds));
      setExpandedDepartments(Array.from(departmentIds));
    }
  }, [searchTerm, filteredUsers, user, orphanedUsers]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await Promise.all([fetchUsers(), fetchPendingRequests()]);
      }
      finally {
        setIsLoading(false);
      }      
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleToken = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      if (token) {
        const name = params.get('name');
        setTokenToApprove(token);
        setRequesterName(name || '');
        setSelectedRole('USER'); // Reset role when opening dialog
        setIsJWTApproval(true); // Set flag for JWT case
        
        // Fetch the pending request to get the email
        try {
          const res = await API.get('/api/users/pending-requests');
          const pendingRequest = res.data.find(req => req.token === token);
          if (pendingRequest) {
            setRequesterEmail(pendingRequest.email);
          }
        } catch (error) {
          console.error('Failed to fetch pending request:', error);
        }
        
        setApproveFromTokenOpen(true);
      }
    };
    handleToken();
  }, [location]);

  useEffect(() => {
    if (hospitals.length > 0 && hospitals.length < 3) {
      const allHospitalIds = hospitals.map(h => h._id);
      const allDepartmentIds = hospitals.flatMap(h => h.departments.map(d => d._id));
      setExpandedHospitals(allHospitalIds);
      setExpandedDepartments(allDepartmentIds);
    }
  }, [hospitals]);

  const fetchPendingRequests = async () => {
    try {
      const res = await API.get('/api/users/pending-requests');
      setPendingRequests(res.data);
    } catch (error) {
      console.error('Fetching pending requests failed:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await API.get('/api/users');
      setUsers(res.data);
    } catch (error) {
      console.error('Fetching users failed:', error);
    }
  };

  const handleExpandHospital = (hospitalId) => {
    setExpandedHospitals(prev =>
      prev.includes(hospitalId)
        ? prev.filter(id => id !== hospitalId)
        : [...prev, hospitalId]
    );
  };

  const handleExpandDepartment = (departmentId) => {
    setExpandedDepartments(prev =>
      prev.includes(departmentId)
        ? prev.filter(id => id !== departmentId)
        : [...prev, departmentId]
    );
  };


  const canManageUserInDepartment = (hospitalId, departmentId) => {
    if (!user) return false;
    if (user.isSuperAdmin) return true;
    return user.roles.some(
      (role) =>
        role.role === 'ADMIN' &&
        role.hospital === hospitalId &&
        role.department === departmentId
    );
  };

  const getDepartmentUsers = (departmentId) => {
    if (searchTerm) {
      return departmentUserMap.get(departmentId) || [];
    }
    return getUsersForDepartment(departmentId);
  };

  const openAddUserDialog = (e, hospitalId, departmentId) => {
    e.stopPropagation();
    setSelectedDepartment({ hospitalId, departmentId });
    setAddDialogOpen(true);
  };

  const handleAddUserToDepartment = async (email, role) => {
    if (!email || !selectedDepartment) return;
     

    const response = await API.post('/api/users/add-by-email', {
      email: email,
      departmentId: selectedDepartment.departmentId,
      hospitalId: selectedDepartment.hospitalId,
      role: role,
    }, { handlingErrorLocally: true });
    fetchUsers(); // Refetch users to update the list
    fetchPendingRequests(); // Refetch pending requests
    fetchPendingRequestsFromContext(); // Update context
    setSuccessMessage(response.data.hebMessage || 'משתמש נוסף בהצלחה');
   
  };

  const handleTokenRequestAction = async (approve) => {
    try {
      await API.post(`/api/users/requests/${tokenToApprove}/action`, {
        approve,
        role: approve ? selectedRole : undefined
      });
      fetchPendingRequests();
      fetchPendingRequestsFromContext(); // Update context
      fetchUsers();
      setApproveFromTokenOpen(false);
      setTokenToApprove(null);
      setSelectedRole('USER'); // Reset role
      navigate('/users', { replace: true });
    } catch (error) {
      console.error('Failed to handle request:', error);
      setApproveFromTokenOpen(false);
    }
  };

  const handleRequestAction = async (requestId, approve) => {
    try {
      await API.post(`/api/users/requests/${requestId}/action`, { 
        approve, 
        role: approve ? selectedRole : undefined 
      });
      fetchPendingRequests();
      fetchPendingRequestsFromContext(); // Update context
      fetchUsers();
    } catch (error) {
      console.error('Failed to handle request:', error);
    }
  };

  const handleRejectRequest = (request) => {
    setRequestToReject(request);
    setRejectConfirmOpen(true);
  };

  const confirmRejectRequest = async () => {
    if (requestToReject) {
      await handleRequestAction(requestToReject._id, false);
      setRejectConfirmOpen(false);
      setRequestToReject(null);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      await API.delete(`/api/users/invitations/${invitationId}`);
      fetchPendingRequests();
      fetchPendingRequestsFromContext(); // Update context
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
    }
  };

  return (
    <CommonLayout>
      <div className="users">
        <div className="search-container" style={{ marginBottom: '15px' }}>
          <InputWithIcon
            icon={searchIcon}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="חיפוש לפי שם או אימייל"
          />
        </div>
      <div className="hospitals-container users-container">
        {isLoading ? (
          <UsersSkeleton />
        ) : (
          <>
        {hospitals.map((hospital) => {
          const authorizedDepartments = hospital.departments.filter(department =>
            canManageUserInDepartment(hospital._id, department._id)
          );

          if (authorizedDepartments.length === 0) {
            return null;
          }

          return (
            <div key={hospital._id} className="hospital-item">
              <div className="hospital-header" onClick={() => handleExpandHospital(hospital._id)}>
                <div className="hospital-title">
                  <FontAwesomeIcon
                    icon={expandedHospitals.includes(hospital._id) ? faChevronDown : faChevronLeft}
                    className="expand-icon"
                  />
                  <h2 style={{ color: "white" }}>{hospital.nameHebrew}</h2>
                </div>
              </div>
              {expandedHospitals.includes(hospital._id) && (
                <div className="departments-list">
                  {authorizedDepartments.map((department) => {
                    const departmentUsers = getDepartmentUsers(department._id);
                    const departmentPendingRequests = pendingRequests.filter(p => p.department && p.department._id === department._id);
                    const hasUsers = departmentUsers.length > 0;
                    const hasPending = departmentPendingRequests.length > 0;

                    return (
                      <div key={department._id} className="department-item-container">
                        <div className="department-item" onClick={() => (hasUsers || hasPending) && handleExpandDepartment(department._id)}>
                          <div className="department-info">
                            {(hasUsers || hasPending) && (
                              <FontAwesomeIcon
                                icon={expandedDepartments.includes(department._id) ? faChevronDown : faChevronLeft}
                                className="expand-icon"
                              />
                            )}
                            <span>{department.nameHebrew}</span>
                          </div>
                          <div className="department-actions">
                            <button className="add-user-btn" onClick={(e) => openAddUserDialog(e, hospital._id, department._id)}>
                              <FontAwesomeIcon icon={faUserPlus} />
                              <span className="add-user-text">הוסף</span>
                            </button>
                          </div>
                        </div>
                        {(hasUsers || hasPending) && expandedDepartments.includes(department._id) && (
                          <div className="users-list">
                            {/* Pending approval users (מבקש להצטרף) - shown first */}
                            {departmentPendingRequests.filter(p => p.type === 'ask-to-join-to-department').map(p => (
                              <div key={p._id} className="user-item pending-item">
                                <span>{`${p.requestedBy?.name || ''} (${p.email})`} רוצה להצטרף</span>
                                <div className="user-actions">
                                  <button className="add-user-btn approve-user-btn" onClick={() => {
                                    setTokenToApprove(p._id);
                                    setRequesterName(p.requestedBy.name);
                                    setRequesterEmail(p.email);
                                    setSelectedRole('USER'); // Reset role when opening dialog
                                    setIsJWTApproval(false); // Set flag for button case
                                    setApproveFromTokenOpen(true);
                                  }}>
                                    <FontAwesomeIcon icon={faCheck} />
                                    <span className="add-user-text">אישור</span>
                                  </button>
                                  <button className="remove-user-btn" onClick={() => handleRejectRequest(p)}>
                                    <FontAwesomeIcon icon={faXmark} />
                                    <span className="remove-user-text">דחייה</span>
                                  </button>
                                </div>
                              </div>
                            ))}
                            
                            {/* Invited users (הוזמן) - shown second */}
                            {departmentPendingRequests.filter(p => p.type === 'invite-user-to-department').map(p => (
                              <div key={p._id} className="user-item pending-item">
                                <span>{p.email} - הוזמן</span>
                                <button className="remove-user-btn" onClick={() => handleRevokeInvitation(p._id)}>
                                  <FontAwesomeIcon icon={faUserXmark} />
                                  <span className="remove-user-text">בטל הזמנה</span>
                                </button>
                              </div>
                            ))}
                            
                            {/* Regular users - shown last, sorted alphabetically */}
                            {[...departmentUsers].sort((a, b) => a.name.localeCompare(b.name, 'he')).map(u => {
                              const adminNote = getAdminNote(u, department);
                              return (
                              <div key={u._id} className="user-item" onClick={() => navigate(`/users/${u._id}`)}>
                                <div className="user-info">
                                  <span>{u.name}</span>
                                  {adminNote && <span className="user-admin-note">{adminNote}</span>}
                                </div>
                                <div onClick={(e) => e.stopPropagation()}>
                                  <UserActionsMenu user={u} currentUser={user} department={department} hospital={hospital} fetchUsers={fetchUsers} />
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {user?.isSuperAdmin && (
          <>
            <div className="hospital-item">
              <div className="hospital-header" onClick={() => handleExpandHospital('super-admins')}>
                <div className="hospital-title">
                  <FontAwesomeIcon
                    icon={expandedHospitals.includes('super-admins') ? faChevronDown : faChevronLeft}
                    className="expand-icon"
                  />
                  <h2 style={{ color: "white" }}>מנהלי mashlom.me</h2>
                </div>
              </div>
              {expandedHospitals.includes('super-admins') && (
                <div className="departments-list">
                  <div className="users-list" style={{ display: 'block' }}>
                    {users.filter(u => u.isSuperAdmin).map(u => (
                      <div key={u._id} className="user-item" onClick={() => navigate(`/users/${u._id}`)}>
                        <span>{u.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {orphanedUsers.length > 0 && (
              <div className="hospital-item">
                <div className="hospital-header" onClick={() => handleExpandHospital('orphaned-users')}>
                  <div className="hospital-title">
                    <FontAwesomeIcon
                      icon={expandedHospitals.includes('orphaned-users') ? faChevronDown : faChevronLeft}
                      className="expand-icon"
                    />
                    <h2 style={{ color: "white" }}>יתומים</h2>
                  </div>
                </div>
                {expandedHospitals.includes('orphaned-users') && (
                  <div className="departments-list">
                    <div className="users-list" style={{ display: 'block' }}>
                      {orphanedUsers.map(u => (
                        <div key={u._id} className="user-item" onClick={() => navigate(`/users/${u._id}`)}>
                          <span>{u.name} ({u.email})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}


        <AddUserConfirmationDialog
          open={addDialogOpen && !successMessage}
          onClose={() => {
            setAddDialogOpen(false);
          }}
          onConfirm={handleAddUserToDepartment}
        />

        <TimedDialog
          open={!!successMessage}
          onClose={() => {
            setSuccessMessage('');
            setAddDialogOpen(false);
            setSelectedDepartment(null);
          }}
          title=""
        >
          <p>{successMessage}</p>
        </TimedDialog>

        <ApproveNewUserRequestDialog
          open={approveFromTokenOpen}
          onClose={() => {
            setApproveFromTokenOpen(false);
            setSelectedRole('USER'); // Reset role when closing dialog
          }}
          onConfirm={() => handleTokenRequestAction(true)}
          title="אישור בקשת הצטרפות"
          onCancel={isJWTApproval ? () => handleTokenRequestAction(false) : () => setApproveFromTokenOpen(false)}
          cancelText={isJWTApproval ? "דחייה" : "ביטול"}
          email={requesterEmail}
          requesterName={requesterName}
          onRoleChange={setSelectedRole}
        />

        <DeleteConfirmationDialog
          open={rejectConfirmOpen}
          onClose={() => {
            setRejectConfirmOpen(false);
            setRequestToReject(null);
          }}
          onConfirm={confirmRejectRequest}
          title={requestToReject ? `האם לדחות את בקשת ההצטרפות של - ${requestToReject.requestedBy?.name} (${requestToReject.email})` : ''}
        />

        {user?.isSuperAdmin && (
          <FloatingAddButton
          entityName="משתמש"
          onClick={() => navigate('/users/new')}
            dataLoaded={true}
          />
        )}
        </>
        )}
        </div>
      </div>
    </CommonLayout>
  );
};

export default Users;